import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logoWhite from "@/assets/images/logo-white.png";

const getFooterLinks = (t: (key: string) => string) => ({
  [t("footerCompany")]: [
    { label: t("footerAboutUs"), path: "/about" },
    { label: t("footerEvents"), path: "/events" },
    { label: t("footerBusiness"), path: "/business" },
    { label: t("footerContact"), path: "/contact" },
  ],
  [t("footerServices")]: [
    { label: t("footerCompanyRegistration"), path: "/services" },
    { label: t("footerRegulatoryCompliance"), path: "/services" },
    { label: t("footerInvestmentFacilitation"), path: "/services" },
    { label: t("footerTenderSupport"), path: "/tenders" },
    { label: t("footerBusinessExpansion"), path: "/services" },
  ],
});

export default function Footer() {
  const { t } = useTranslation();
  const footerLinks = getFooterLinks(t);

  return (
    <footer className="bg-[#0A1628] text-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1 space-y-5">
            <Link to="/">
              <img
                alt="Africa Investment Portal"
                className="h-24 w-auto object-contain brightness-0 invert"
                src={logoWhite}
              />
            </Link>
            <p className="text-white/40 text-sm leading-relaxed">
              {t("footerTagline")}
            </p>
            <div className="flex gap-3">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer w-9 h-9 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white/60 hover:bg-[#00C2C2] hover:border-[#00C2C2] hover:text-white transition-all duration-300"
              >
                <i className="ri-linkedin-fill text-sm" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer w-9 h-9 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white/60 hover:bg-[#00C2C2] hover:border-[#00C2C2] hover:text-white transition-all duration-300"
              >
                <i className="ri-twitter-x-fill text-sm" />
              </a>
              <a
                href="https://wa.me/250700000000"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer w-9 h-9 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white/60 hover:bg-[#00C2C2] hover:border-[#00C2C2] hover:text-white transition-all duration-300"
              >
                <i className="ri-whatsapp-line text-sm" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          {Object.entries(footerLinks).map(([section, links]) => (
            <div key={section}>
              <h4 className="text-xs font-bold tracking-[0.2em] text-white/40 uppercase mb-5">
                {section}
              </h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.path}
                      className="text-white/50 hover:text-white text-sm transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact */}
          <div>
            <h4 className="text-xs font-bold tracking-[0.2em] text-white/40 uppercase mb-5">
              {t("navContact")}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-white/50">
                <i className="ri-map-pin-line mt-0.5 text-[#00C2C2] flex-shrink-0" />
                <span>{t("navLocation")}</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/50">
                <i className="ri-mail-line mt-0.5 text-[#00C2C2]" />
                <span>{t("navEmail")}</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/50">
                <i className="ri-phone-line mt-0.5 text-[#00C2C2]" />
                <span>{t("navPhone")}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">
            {t("footerCopyright", { year: new Date().getFullYear() })}
          </p>
          <div className="flex gap-6">
            <Link
              to="/contact"
              className="text-white/30 hover:text-white/60 text-xs transition-colors"
            >
              {t("footerPrivacyPolicy")}
            </Link>
            <Link
              to="/contact"
              className="text-white/30 hover:text-white/60 text-xs transition-colors"
            >
              {t("footerTermsOfService")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}